import { Box, Flex } from '@chakra-ui/react';
import type { Msg, Session } from '@halo/chat-sdk-react';
import { MsgPayloadType, StateEnum, useAppStore, useIsMyMsg } from '@halo/chat-sdk-react';
import { ActionMediaIcon } from '@halo/ui-chat-react';
import type { ReactNode } from 'react';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { useMediaError } from '@/scripts/hooks';

interface MsgMediaCardContentProps {
  title: ReactNode;
  description: ReactNode;
  isError?: boolean;
  callback?: boolean;
  session?: Session;
  message: Msg;
}
export const MsgMediaCardContent = ({
  title,
  description,
  isError,
  callback,
  session,
  message,
}: MsgMediaCardContentProps) => {
  const { mediaCall, mediaActivatedId } = useAppStore(
    useShallowEnhance(['mediaCall', 'mediaActivatedId'])
  );
  const { mediaError } = useMediaError();
  const [loading, setLoading] = useState(false);
  const isMe = useIsMyMsg(message);
  const { type } = message.payload;

  const disabledClick = useMemo(() => {
    return (
      session?.user_info?.state === StateEnum.DELETE_ACCOUNT ||
      !callback ||
      mediaActivatedId ||
      loading
    );
  }, [callback, loading, mediaActivatedId, session?.user_info?.state]);

  const handleCall = useCallback(() => {
    if (disabledClick) return;
    setLoading(true);
    mediaCall({
      session_id: session!.chat_id,
      type: type === 1 ? MsgPayloadType.MEDIA_AUDIO_CALL : MsgPayloadType.MEDIA_VIDEO_CALL,
      membersId: [session!.user_info!.user_id],
    })
      .catch((error) => {
        mediaError(error, 'call');
      })
      .finally(() => setLoading(false));
  }, [disabledClick, mediaCall, mediaError, session, type]);

  return (
    <Flex gap="2" onClick={handleCall} cursor={callback ? 'pointer' : 'initial'}>
      <ActionMediaIcon payload_type={message.payload_type} type={type} isMe={isMe} />
      <Box>
        <Box fontWeight="semibold" fontSize="md" color={isError ? 'fg.error' : 'fg.subtle'}>
          {title}
        </Box>
        <Box fontWeight="medium" fontSize="small" color="fg.subtle">
          {description}
        </Box>
      </Box>
    </Flex>
  );
};
